/**
 * Mount point for /foundation/admin/technology-lifecycle: the shared
 * AdminListView over the register, with a file import above it.
 *
 * The import is checked first: choosing a file runs a dry run that says
 * what each row would do, and the import itself is all or nothing,
 * offered only when no row has an error.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { currentAuthToken } from "shared/api/client";
import { useMe } from "shared/api/useMe";
import { useToast } from "shared/toast/ToastProvider";

import { AdminListView } from "./AdminListView";
import { technologyLifecycleAdapter } from "./technologyLifecycleAdapter";

const P = "modules.foundation.admin.technologyLifecycle.import";
const URL = `${technologyLifecycleAdapter.endpoint}/import`;
const TEMPLATE =
  "manufacturer,model,mainstream_support_end,extended_support_end,source,review_due,notes,replacement_cost_sar\n" +
  "ExampleCo,Example model,2028-12-31,2031-12-31,The maker's end-of-support notice,2027-06-30,,45000\n";

interface ImportRow {
  row: number;
  manufacturer: string;
  model: string;
  action: "insert" | "update" | "error";
  error: string | null;
}

export interface ImportResult {
  filename: string;
  rows: ImportRow[];
  inserted: number;
  updated: number;
  errors: number;
  ignored_columns: string[];
  committed: boolean;
}

class ImportError extends Error {}

async function send(file: File, commit: boolean): Promise<ImportResult> {
  const token = currentAuthToken();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${URL}?commit=${commit}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const detail =
      body && typeof (body as { detail?: unknown }).detail === "string"
        ? (body as { detail: string }).detail
        : res.statusText;
    throw new ImportError(detail);
  }
  return body as ImportResult;
}

function ImportPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const check = useMutation<ImportResult, ImportError, File>({
    meta: { handled: true },
    mutationFn: (f) => send(f, false),
    onSuccess: setResult,
    onError: (err) => toast.push({ tone: "error", message: err.message }),
  });
  const commit = useMutation<ImportResult, ImportError, File>({
    meta: { handled: true },
    mutationFn: (f) => send(f, true),
    onSuccess: (out) => {
      toast.push({
        tone: "success",
        message: t(`${P}.done`, { inserted: out.inserted, updated: out.updated }),
      });
      setResult(null);
      setFile(null);
      void qc.invalidateQueries({ queryKey: technologyLifecycleAdapter.queryKey });
    },
    onError: (err) => toast.push({ tone: "error", message: err.message }),
  });
  const errorRows = result ? result.rows.filter((r) => r.action === "error") : [];
  return (
    <section
      data-testid="tle-import"
      className="space-y-2 rounded border border-slate-200 bg-white p-3 text-sm"
    >
      <h2 className="text-sm font-semibold text-slate-900">{t(`${P}.heading`)}</h2>
      <p className="text-xs text-slate-600">
        {t(`${P}.body`)}{" "}
        <a
          href={`data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE)}`}
          download="technology-lifecycle-template.csv"
          className="text-brand-brassDark hover:underline"
        >
          {t(`${P}.template`)}
        </a>
      </p>
      <input
        type="file"
        accept=".csv,text/csv"
        data-testid="tle-import-file"
        className="block text-xs"
        disabled={check.isPending || commit.isPending}
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          setFile(f);
          setResult(null);
          if (f) check.mutate(f);
          e.target.value = "";
        }}
      />
      {result && file && (
        <div data-testid="tle-import-result" className="space-y-2">
          <p className="text-xs text-slate-700">
            {t(`${P}.summary`, {
              file: result.filename,
              inserted: result.inserted,
              updated: result.updated,
              errors: result.errors,
            })}
          </p>
          {result.ignored_columns.length > 0 && (
            <p className="text-xs text-amber-800">
              {t(`${P}.ignored`, { columns: result.ignored_columns.join(", ") })}
            </p>
          )}
          {errorRows.length > 0 && (
            <ul
              data-testid="tle-import-errors"
              className="space-y-0.5 text-xs text-rose-800"
            >
              {errorRows.map((r) => (
                <li key={r.row}>
                  {t(`${P}.rowError`, {
                    row: r.row,
                    product: `${r.manufacturer} ${r.model}`.trim(),
                    error: r.error ?? "",
                  })}
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            data-testid="tle-import-commit"
            disabled={result.errors > 0 || commit.isPending}
            onClick={() => commit.mutate(file)}
            className="rounded bg-brand-brass px-3 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {result.errors > 0
              ? t(`${P}.fixFirst`)
              : t(`${P}.commit`, { count: result.inserted + result.updated })}
          </button>
        </div>
      )}
    </section>
  );
}

export function TechnologyLifecycleAdminPage() {
  const me = useMe();
  return (
    <div className="space-y-4">
      {me.data?.is_admin && <ImportPanel />}
      <AdminListView adapter={technologyLifecycleAdapter} />
    </div>
  );
}
